import { View, Text, Platform } from 'react-native';
import Head from 'expo-router/head';
import { FileText } from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { usePublicThemeColors } from '@/lib/useThemeColors';
import { useTrackScreen } from '@/lib/analytics';
import { PublicLayout } from '@/components/PublicLayout';
import { LegalLink } from '@/components/legal/LegalBlocks';
import { OPERATOR_PARTICULARS } from '@/lib/legal/operator';

// Content mirrors the master document at docs/legal/terms-of-service.md — update both together.

interface SectionProps {
  number: number;
  title: string;
  children: React.ReactNode;
  colors: ReturnType<typeof usePublicThemeColors>;
  delay: number;
}

function Section({ number, title, children, colors, delay }: SectionProps) {
  return (
    <Animated.View entering={FadeInDown.delay(delay).duration(500)} style={{ marginBottom: 24 }}>
      <Text
        style={{ color: colors.text.primary }}
        className="text-base font-bold mb-2"
      >
        {number}. {title}
      </Text>
      <View>{children}</View>
    </Animated.View>
  );
}

function Paragraph({ children, colors }: { children: React.ReactNode; colors: ReturnType<typeof usePublicThemeColors> }) {
  return (
    <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed mb-2">
      {children}
    </Text>
  );
}

export default function TermsScreen() {
  useTrackScreen('Terms');
  const colors = usePublicThemeColors();

  return (
    <PublicLayout
      title="Terms of Service"
      titleIcon={<FileText size={22} color={colors.primary[600]} />}
    >
      {Platform.OS === 'web' && (
        <Head>
          <title>Terms of Service — Inverio</title>
          <meta name="description" content="Terms of Service for Inverio, the AI-assisted investment readiness platform. Governed by Swiss law." />
          <link rel="canonical" href="https://inverio.ai/terms" />
          <meta property="og:title" content="Terms of Service — Inverio" />
          <meta property="og:description" content="Terms of Service for Inverio, the AI-assisted investment readiness platform. Governed by Swiss law." />
          <meta property="og:url" content="https://inverio.ai/terms" />
        </Head>
      )}
      {/* Last Updated */}
      <Animated.View entering={FadeInDown.delay(100).duration(500)} className="mb-6">
        <Text style={{ color: colors.text.tertiary }} className="text-sm">
          Last updated: October 24, 2026
        </Text>
      </Animated.View>

      {/* Introduction */}
      <Animated.View entering={FadeInDown.delay(150).duration(500)} className="mb-6">
        <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
          These Terms of Service ("Terms") govern your access to and use of the Inverio platform, including our website, web application, mobile applications, and related services (collectively, the "Service").
        </Text>
      </Animated.View>

      <Section number={1} title="Operator & Acceptance" colors={colors} delay={200}>
        <Paragraph colors={colors}>
          Inverio is a service operated by A. Kouadio INVARK, a sole proprietorship (raison individuelle) registered in Switzerland ("Inverio," "we," "us," "our"). By creating an account, accessing, or using the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you use the Service on behalf of a company, you represent that you are authorized to bind that company, and "you" refers to that company. If you do not agree to these Terms, you must not use the Service.
        </Paragraph>
      </Section>

      <Section number={2} title="The Service & Beta Status" colors={colors} delay={225}>
        <Paragraph colors={colors}>
          Inverio is an AI-assisted investment readiness platform connecting startup founders and investors. The Service includes investment readiness scoring, startup assessments, due diligence tools, data rooms, and investor–startup matching.
        </Paragraph>
        <Paragraph colors={colors}>
          The Service is currently offered as a paid beta. During the beta: features may be added, modified, or removed without notice; occasional interruptions and defects are expected; and the Service is provided without any service level agreement. We will notify you of material changes that affect paid features.
        </Paragraph>
      </Section>

      <Section number={3} title="No Professional or Investment Advice" colors={colors} delay={250}>
        <Paragraph colors={colors}>
          Inverio is NOT a financial adviser, investment adviser, financial intermediary, broker-dealer, or fiduciary under the Swiss Financial Services Act (FinSA) or the laws of any other jurisdiction. All scores, analyses, reports, matches, and other outputs of the Service are informational only and do not constitute financial, investment, legal, or tax advice, nor a recommendation, offer, or solicitation to buy or sell any security or make any investment.
        </Paragraph>
        <Paragraph colors={colors}>
          Any decision you make based on information from the Service is made at your own risk. Consult qualified professionals before making investment decisions.
        </Paragraph>
      </Section>

      <Section number={4} title="AI-Generated Content & Human Review" colors={colors} delay={275}>
        <Paragraph colors={colors}>
          Parts of the Service use artificial intelligence, including large language models, to generate scores, assessments, and insights. AI-generated content is clearly labelled within the Service. AI outputs are advisory: they never make automated decisions with legal or similarly significant effect, and investors always make their own decisions.
        </Paragraph>
        <Paragraph colors={colors}>
          You have the right to request human review of any AI-generated score or assessment concerning you or your company. Details, including how our AI systems work and how to exercise this right, are available at inverio.ai/ai-disclosure.
        </Paragraph>
      </Section>

      <Section number={5} title="Accounts & Eligibility" colors={colors} delay={300}>
        <Paragraph colors={colors}>
          You must be at least 18 years old to use the Service. You must provide accurate and complete registration information and keep it current. Each individual may maintain only one account. You are responsible for safeguarding your credentials and for all activity under your account, and you must notify us promptly of any unauthorized use.
        </Paragraph>
      </Section>

      <Section number={6} title="Subscriptions, Billing & Cancellation" colors={colors} delay={325}>
        <Paragraph colors={colors}>
          Certain features require a paid subscription. Subscription tiers differ in the features and usage volumes they include (for example, the number of AI due-diligence conversations or insights per period, and whether your score can be listed in the investor marketplace); the current tiers and limits are shown in the Service. Prices are displayed before purchase. Payments are processed by Stripe; we do not store your card details. Subscriptions renew automatically at the end of each billing period unless cancelled beforehand.
        </Paragraph>
        <Paragraph colors={colors}>
          You may cancel at any time in your account settings; cancellation takes effect at the end of the current billing period, and you retain access to paid features until then. Fees for started billing periods are not refunded, except where required by law. We may change prices and included usage volumes with at least 30 days' notice; changes apply from your next renewal. Free tiers or trial access may be modified or withdrawn at any time. Our <LegalLink href="/refunds">Refund Policy</LegalLink> explains how cancellation, refunds, plan changes and failed payments work.
        </Paragraph>
      </Section>

      <Section number={7} title="Waitlist, Early-Bird Discounts & Referrals" colors={colors} delay={340}>
        <Paragraph colors={colors}>
          Waitlist: joining the waitlist requires a verified email address (double opt-in). Your waitlist position is assigned at verified signup, and referring others moves you up the list. Joining the waitlist does not come with a discount. If you joined on or before 17 September 2026 and we told you a specific early-bird tier in your confirmation email, that tier is still honored as stated there: it is personal, non-transferable, applies to the plans and billing periods stated in that email, and cannot be combined with other discounts unless we say otherwise.
        </Paragraph>
        <Paragraph colors={colors}>
          Referrals: when someone signs up using your referral code and converts to a paid plan within the stated conversion window, you receive the referral reward communicated in the Service (currently a percentage discount on your next billing cycle). Referral rewards are one-time per referred user, expire if unused within the stated validity period, have no cash value, and are void where abuse is detected (self-referrals, fake accounts, automated signups). We may modify or end the referral program prospectively; earned rewards remain valid.
        </Paragraph>
      </Section>

      <Section number={8} title="Your Content" colors={colors} delay={350}>
        <Paragraph colors={colors}>
          You retain all ownership rights in the content you upload to the Service (pitch decks, financial data, data room documents, interview answers, notes, and other materials, collectively "User Content"). You grant us a non-exclusive, worldwide license to host, process, analyze, and display User Content solely to provide the Service. Uploaded documents are scanned for malware and may be quarantined. You are responsible for your User Content, including having the necessary rights to upload it — in particular for documents containing information about third parties such as your team members.
        </Paragraph>
        <Paragraph colors={colors}>
          Keep your own copies. The Service is not a backup or archival service. You are responsible for maintaining independent copies of all documents you upload; we do not guarantee against loss of data.
        </Paragraph>
      </Section>

      <Section number={9} title="Due Diligence, Data Rooms & Confidentiality" colors={colors} delay={365}>
        <Paragraph colors={colors}>
          Founders control access to their data room: investors see data-room documents only after the founder grants that investor access, and grants can be revoked or expire. If you are an investor receiving access to a founder's materials, you agree to use them solely to evaluate a potential investment in that startup, to keep them confidential with at least reasonable care, and not to use them to compete with the startup or share them outside your due-diligence team. In a shared due-diligence workspace, materials and non-private notes are visible to the collaborators the workspace owner invites, who are bound by the same obligations.
        </Paragraph>
        <Paragraph colors={colors}>
          Founders acknowledge that: investors with whom they have a concrete relationship (submission received, dealflow, active due diligence) can see their score and its breakdown; founders can see that a due-diligence workspace exists on their startup and who opened it, but not its contents; and investors' own notes, analyses, and conclusions belong to the investor. These Terms create no confidentiality obligation on Inverio beyond the Privacy Policy, and no NDA between users beyond this Section — parties may conclude their own agreements.
        </Paragraph>
      </Section>

      <Section number={10} title="API Keys & Connected Tools" colors={colors} delay={370}>
        <Paragraph colors={colors}>
          You may create API keys and connect external tools (including AI assistants via the Model Context Protocol) to your account. Anything a connected tool does with your account is attributed to you: keep keys secret, grant only the scopes you need, and revoke keys you no longer use. Connected tools can access only data your account can already access. We are not responsible for how external tools you connect handle your data.
        </Paragraph>
      </Section>

      <Section number={11} title="Acceptable Use" colors={colors} delay={375}>
        <Paragraph colors={colors}>
          You agree not to: (a) share account credentials or circumvent access controls; (b) access the Service with automated tools, bots, or scrapers; (c) resell, redistribute, or commercially exploit scores, analyses, or other outputs without our written consent; (d) attempt to reverse-engineer or extract our scoring methodology, prompts, or models; (e) upload malicious code or unlawful content; (f) use the Service to send spam; (g) misrepresent your identity, your company, or your data — including submitting false or misleading information for scoring; or (h) violate applicable law.
        </Paragraph>
        <Paragraph colors={colors}>
          We may suspend or terminate accounts that violate these Terms.
        </Paragraph>
      </Section>

      <Section number={12} title="Third-Party Services & Integrations" colors={colors} delay={400}>
        <Paragraph colors={colors}>
          The Service relies on third-party providers (including Supabase for infrastructure hosted in the EU, Stripe for payments, and EU-hosted AI providers) acting as our processors, as described in our Privacy Policy.
        </Paragraph>
        <Paragraph colors={colors}>
          The Service also offers optional integrations you can connect yourself, such as Google Drive and Dropbox for importing documents. These integrations are governed by the respective provider's terms; you can disconnect them at any time. We are not responsible for third-party services.
        </Paragraph>
      </Section>

      <Section number={13} title="Intellectual Property" colors={colors} delay={425}>
        <Paragraph colors={colors}>
          The Service — including the scoring methodology, software, design, and branding — is and remains the exclusive property of the operator or its licensors. You receive a limited, non-exclusive, non-transferable right to use the Service and its outputs for your internal business purposes for the duration of your subscription. AI-generated scores and reports concerning your company may be shared in the course of ordinary fundraising or investment activity (for example, sharing a due diligence report with co-investors), but may not be resold or published commercially without our consent.
        </Paragraph>
      </Section>

      <Section number={14} title="Availability & Support" colors={colors} delay={450}>
        <Paragraph colors={colors}>
          We aim for high availability but do not guarantee uninterrupted access. The Service may be unavailable due to maintenance, updates, or events outside our control. Support is available at support@inverio.ai; we aim to respond within two business days.
        </Paragraph>
      </Section>

      <Section number={15} title="Termination" colors={colors} delay={475}>
        <Paragraph colors={colors}>
          You may request deletion of your account at any time, in the app or by email to privacy@inverio.ai. The deletion runs 30 days after your request, and you can cancel it until then; on a verified request by email we can run it immediately (see the Privacy Policy, Section 14). We may suspend or terminate your access for material breach of these Terms, with notice where practicable. Upon termination, your data is handled as described in the Privacy Policy, including your right to export it beforehand. Sections 3, 8, 9, 13, 16, 17, and 19 survive termination.
        </Paragraph>
      </Section>

      <Section number={16} title="Disclaimers" colors={colors} delay={500}>
        <Paragraph colors={colors}>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE". TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT SCORES OR ANALYSES ARE ACCURATE, COMPLETE, OR SUITABLE FOR ANY PARTICULAR INVESTMENT DECISION, OR THAT THE SERVICE WILL BE ERROR-FREE OR UNINTERRUPTED.
        </Paragraph>
      </Section>

      <Section number={17} title="Limitation of Liability" colors={colors} delay={525}>
        <Paragraph colors={colors}>
          TO THE MAXIMUM EXTENT PERMITTED BY SWISS LAW, OUR TOTAL LIABILITY FOR ALL CLAIMS ARISING FROM OR RELATING TO THE SERVICE IS LIMITED TO THE FEES YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM. WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES, INCLUDING LOST PROFITS, LOST DATA, LOST BUSINESS OPPORTUNITIES, OR INVESTMENT LOSSES. NOTHING IN THESE TERMS EXCLUDES LIABILITY FOR GROSS NEGLIGENCE, UNLAWFUL INTENT, OR ANY LIABILITY THAT CANNOT BE EXCLUDED UNDER SWISS LAW.
        </Paragraph>
      </Section>

      <Section number={18} title="Assignment & Corporate Transition" colors={colors} delay={550}>
        <Paragraph colors={colors}>
          We may assign or transfer these Terms, and the operation of the Service, to a legal successor — in particular to a Swiss company (such as a GmbH or AG) formed to continue the Inverio business — without your consent, provided your rights under these Terms are not materially reduced. We will notify you of any such transfer. You may not assign these Terms without our written consent.
        </Paragraph>
      </Section>

      <Section number={19} title="Governing Law & Jurisdiction" colors={colors} delay={575}>
        <Paragraph colors={colors}>
          These Terms are governed by the substantive laws of Switzerland, excluding its conflict-of-laws rules and the UN Convention on Contracts for the International Sale of Goods (CISG). The courts at the operator's registered seat in Switzerland have exclusive jurisdiction, subject to any mandatory consumer venue provisions.
        </Paragraph>
      </Section>

      <Section number={20} title="Changes to These Terms" colors={colors} delay={600}>
        <Paragraph colors={colors}>
          We may modify these Terms. For material changes we will give at least 30 days' notice via the Service or email. Continued use after the effective date constitutes acceptance; if you do not agree, stop using the Service and cancel your subscription before the changes take effect.
        </Paragraph>
      </Section>

      <Section number={21} title="Contact" colors={colors} delay={625}>
        <Paragraph colors={colors}>
          If you have any questions about these Terms, please contact us:
        </Paragraph>
        <View
          className="p-4 rounded-2xl mt-2"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <Text style={{ color: colors.text.primary }} className="font-semibold mb-1">
            Inverio — a service of A. Kouadio INVARK, Switzerland
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-1">
            {OPERATOR_PARTICULARS}
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-1">
            General inquiries: support@inverio.ai
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm">
            Privacy inquiries: privacy@inverio.ai
          </Text>
        </View>
      </Section>
    </PublicLayout>
  );
}
